import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  loadPreAuthMetrics, 
  clearPreAuthMetrics, 
  fetchPreAuthMetricsFromBackend,
  clearPreAuthEmail,
  PreAuthMetric 
} from "@/lib/preAuthMetrics";
import { fetchMetricDefinitions } from "@/lib/metricDefinitions";
import { toast } from "sonner";

const TRANSFER_FLAG_KEY = "gitfit_metrics_transferred";

/**
 * Hook to transfer pre-auth metrics to user account after sign up/sign in
 * This should be called when a user becomes authenticated
 * Now checks BOTH localStorage AND backend for pending metrics
 */
export function useTransferPreAuthMetrics(userId: string | undefined) {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    if (!userId) return;
    
    // Check if we've already transferred for this session
    const transferFlag = localStorage.getItem(TRANSFER_FLAG_KEY);
    if (transferFlag === userId) {
      console.log("[PreAuthMetrics] Already transferred for this user, skipping");
      return;
    }

    const transferMetrics = async () => {
      // Check BOTH localStorage and backend for metrics
      const localMetrics = loadPreAuthMetrics();
      const backendMetrics = await fetchPreAuthMetricsFromBackend();
      
      // Merge metrics, prioritizing localStorage (most recent)
      const allMetrics = [...localMetrics];
      const localKeys = new Set(localMetrics.map(m => m.key));
      
      // Add backend metrics that aren't already in local storage
      for (const metric of backendMetrics) {
        if (!localKeys.has(metric.key)) {
          allMetrics.push(metric);
        }
      }
      
      if (allMetrics.length === 0) {
        console.log("[PreAuthMetrics] No metrics to transfer from localStorage or backend");
        // Still set the flag to prevent checking again
        localStorage.setItem(TRANSFER_FLAG_KEY, userId);
        return;
      }

      if (backendMetrics.length > 0) {
        console.log(`[PreAuthMetrics] Found ${backendMetrics.length} metrics from backend, ${localMetrics.length} from localStorage`);
      }

      console.log(`[PreAuthMetrics] Transferring ${allMetrics.length} total metrics to user account`);

      try {
        // Check if user already had an account by looking for existing custom metrics
        // Note: We don't check user_goals because it's auto-created for new users via trigger
        const { data: preExistingMetrics } = await supabase
          .from("custom_metrics")
          .select("metric_key")
          .eq("user_id", userId)
          .limit(1);

        // Additionally, check if the user account was created more than 5 minutes ago
        // This helps distinguish between a brand new signup and a returning user
        const { data: authUser } = await supabase.auth.getUser();
        const accountAge = authUser?.user?.created_at 
          ? Date.now() - new Date(authUser.user.created_at).getTime()
          : 0;
        const isRecentSignup = accountAge < 5 * 60 * 1000; // Less than 5 minutes old

        // If user has custom metrics AND account is not recent, they had an account before
        const userAlreadyHadAccount = (preExistingMetrics && preExistingMetrics.length > 0) && !isRecentSignup;
        
        if (userAlreadyHadAccount) {
          console.log("[PreAuthMetrics] User already had an account, not transferring onboarding metrics");
          clearPreAuthMetrics();
          localStorage.setItem(TRANSFER_FLAG_KEY, userId);
          toast.info("Welcome back! Your existing metrics were preserved.");
          return;
        }

        // Separate custom metrics from built-in metrics
        // Custom metrics: start with 'custom_'
        // Built-in metrics: everything else (protein, water, steps, etc.)
        const customMetrics = allMetrics.filter(m => m.key.startsWith('custom_'));
        const builtInMetrics = allMetrics.filter(m => !m.key.startsWith('custom_'));
        
        console.log("[PreAuthMetrics] Custom metrics to transfer:", customMetrics);
        console.log("[PreAuthMetrics] Built-in metrics (goals only):", builtInMetrics);

        // Get existing custom metrics to find available slots and check for duplicates
        const { data: existingMetrics } = await supabase
          .from("custom_metrics")
          .select("metric_key, metric_name")
          .eq("user_id", userId);

        const usedSlots = new Set(existingMetrics?.map(m => m.metric_key) || []);
        const existingNames = new Set(existingMetrics?.map(m => m.metric_name.toLowerCase()) || []);
        console.log("[PreAuthMetrics] Used custom metric slots:", Array.from(usedSlots));
        console.log("[PreAuthMetrics] Existing metric names:", Array.from(existingNames));

        // Transfer custom metrics
        let successCount = 0;
        for (const metric of customMetrics) {
          // Skip if a metric with this name already exists
          if (existingNames.has(metric.name.toLowerCase())) {
            console.log(`[PreAuthMetrics] Metric "${metric.name}" already exists, skipping`);
            continue;
          }
          
          // Find an available slot if this one is taken
          let targetKey = metric.key;
          if (usedSlots.has(targetKey)) {
            // Find first available slot
            for (let i = 1; i <= 10; i++) {
              const candidateKey = `custom_${i}`;
              if (!usedSlots.has(candidateKey)) {
                targetKey = candidateKey;
                break;
              }
            }
            
            if (targetKey === metric.key) {
              console.warn("[PreAuthMetrics] No available slots for custom metric:", metric.name);
              continue;
            }
            
            console.log(`[PreAuthMetrics] Reassigning ${metric.key} to ${targetKey}`);
          }

          // Save custom metric (use upsert to handle edge case where metric already exists)
          console.log(`[PreAuthMetrics] Saving custom metric:`, {
            key: targetKey,
            name: metric.name,
            type: metric.type,
            unit: metric.unit,
            emoji: metric.emoji,
            inverse: metric.inverse
          });
          
          const { error: metricError } = await supabase
            .from("custom_metrics")
            .upsert({
              user_id: userId,
              metric_key: targetKey,
              metric_name: metric.name,
              metric_emoji: metric.emoji,
              metric_unit: metric.unit,
              metric_type: metric.type,
              is_inverse: metric.inverse,
              is_relative: metric.isRelative || false,
            }, {
              onConflict: 'user_id,metric_key',
            });

          if (metricError) {
            console.error("[PreAuthMetrics] Failed to save metric:", metricError);
            continue;
          }
          
          console.log(`[PreAuthMetrics] Successfully saved custom metric: ${metric.name} (${metric.type})`);


          usedSlots.add(targetKey);
          successCount++;

          // Save goal for custom metric (both numeric and boolean)
          if (metric.goal > 0) {
            const goalKey = `${targetKey}_goal`;
            console.log(`[PreAuthMetrics] Saving goal for ${targetKey}: ${metric.goal}`);
            
            // First, ensure user has a goals row
            const { data: existingGoals } = await supabase
              .from("user_goals")
              .select("*")
              .eq("user_id", userId)
              .maybeSingle();
            
            if (existingGoals) {
              // Update existing row
              const { error: goalError } = await supabase
                .from("user_goals")
                .update({ [goalKey]: metric.goal })
                .eq("user_id", userId);
              
              if (goalError) {
                console.error("[PreAuthMetrics] Failed to update goal:", goalError);
              } else {
                console.log(`[PreAuthMetrics] Successfully updated goal ${goalKey} = ${metric.goal}`);
              }
            } else {
              // Create new row with this goal
              const { error: goalError } = await supabase
                .from("user_goals")
                .insert({
                  user_id: userId,
                  [goalKey]: metric.goal,
                });
              
              if (goalError) {
                console.error("[PreAuthMetrics] Failed to insert goal:", goalError);
              } else {
                console.log(`[PreAuthMetrics] Successfully inserted goal ${goalKey} = ${metric.goal}`);
              }
            }
          }
        }

        // Transfer goals AND enable built-in metrics
        // Track HealthKit metrics for notification (declare outside try block)
        const healthKitMetrics: string[] = [];
        
        if (builtInMetrics.length > 0) {
          const goalsToSet: Record<string, number> = {};
          
          try {
            const metricDefinitions = await fetchMetricDefinitions();
            console.log(`[PreAuthMetrics] Fetched ${Object.keys(metricDefinitions).length} metric definitions`);
            
            // Organize built-in metrics by category for metric_preferences
            const nutritionMetrics: string[] = [];
            const activityMetrics: string[] = [];
            const resultsMetrics: string[] = [];
            
            for (const metric of builtInMetrics) {
              // Get the metric definition to determine category
              const metricDef = metricDefinitions[metric.key];
              
              if (metricDef) {
                // Track if this is a HealthKit metric
                if (metricDef.isHealthKit && metricDef.label) {
                  healthKitMetrics.push(metricDef.label);
                  console.log(`[PreAuthMetrics] Found HealthKit metric: ${metricDef.label}`);
                }
                
                // Add to appropriate category array
                if (metricDef.category === 'nutrition' && !nutritionMetrics.includes(metric.key)) {
                  nutritionMetrics.push(metric.key);
                  console.log(`[PreAuthMetrics] Adding ${metric.key} to nutrition_metrics`);
                } else if (metricDef.category === 'activity_sleep' && !activityMetrics.includes(metric.key)) {
                  activityMetrics.push(metric.key);
                  console.log(`[PreAuthMetrics] Adding ${metric.key} to activity_sleep_metrics`);
                } else if (metricDef.category === 'results' && !resultsMetrics.includes(metric.key)) {
                  resultsMetrics.push(metric.key);
                  console.log(`[PreAuthMetrics] Adding ${metric.key} to results_metrics`);
                }
                
                // Set up goals
                if (metric.type === "numeric" && metric.goal > 0) {
                  if (metricDef.goalKey) {
                    console.log(`[PreAuthMetrics] Found goalKey for ${metric.key}: ${metricDef.goalKey}`);
                    goalsToSet[metricDef.goalKey] = metric.goal;
                  } else {
                    // Fallback to key_goal format if no goal key defined
                    console.warn("[PreAuthMetrics] No goalKey found for metric:", metric.key, "Using fallback");
                    const goalKey = `${metric.key}_goal`;
                    goalsToSet[goalKey] = metric.goal;
                  }
                }
                
                // Special handling for calories: also set calories_inverse
                if (metric.key === 'calories') {
                  (goalsToSet as Record<string, any>).calories_inverse = metric.inverse;
                  console.log(`[PreAuthMetrics] Setting calories_inverse = ${metric.inverse}`);
                }
              } else {
                console.warn("[PreAuthMetrics] Metric definition not found for:", metric.key);
              }
            }
            
            // Fetch current metric preferences to merge with existing enabled metrics
            const { data: currentPrefs } = await supabase
              .from("metric_preferences")
              .select("*")
              .eq("user_id", userId)
              .maybeSingle();
            
            // Merge with existing preferences
            const mergedNutrition = [...new Set([...(currentPrefs?.nutrition_metrics || []), ...nutritionMetrics])];
            const mergedActivity = [...new Set([...(currentPrefs?.activity_sleep_metrics || []), ...activityMetrics])];
            const mergedResults = [...new Set([...(currentPrefs?.results_metrics || []), ...resultsMetrics])];
            
            // Update metric_preferences to enable the built-in metrics
            if (nutritionMetrics.length > 0 || activityMetrics.length > 0 || resultsMetrics.length > 0) {
              const { error: prefsError } = await supabase
                .from("metric_preferences")
                .upsert({
                  user_id: userId,
                  nutrition_metrics: mergedNutrition,
                  activity_sleep_metrics: mergedActivity,
                  results_metrics: mergedResults,
                });
              
              if (prefsError) {
                console.error("[PreAuthMetrics] Failed to enable built-in metrics:", prefsError);
              } else {
                console.log("[PreAuthMetrics] Successfully enabled built-in metrics in preferences");
              }
            }
          } catch (error) {
            console.error("[PreAuthMetrics] Failed to fetch metric definitions:", error);
            // Fallback: use key_goal format for all
            for (const metric of builtInMetrics) {
              if (metric.type === "numeric" && metric.goal > 0) {
                const goalKey = `${metric.key}_goal`;
                goalsToSet[goalKey] = metric.goal;
              }
              // Also handle calories_inverse in fallback
              if (metric.key === 'calories') {
                (goalsToSet as Record<string, any>).calories_inverse = metric.inverse;
                console.log(`[PreAuthMetrics] (fallback) Setting calories_inverse = ${metric.inverse}`);
              }
            }
          }

          if (Object.keys(goalsToSet).length > 0) {
            console.log("[PreAuthMetrics] Goals to save:", goalsToSet);
            
            // Check if user has goals row
            const { data: existingGoals } = await supabase
              .from("user_goals")
              .select("*")
              .eq("user_id", userId)
              .maybeSingle();
            
            if (existingGoals) {
              // Update existing row with new goals
              const { error: goalError } = await supabase
                .from("user_goals")
                .update(goalsToSet)
                .eq("user_id", userId);
              
              if (goalError) {
                console.error("[PreAuthMetrics] Failed to update built-in goals:", goalError);
              } else {
                console.log("[PreAuthMetrics] Successfully updated built-in goals");
                successCount += builtInMetrics.length;
              }
            } else {
              // Create new row with these goals
              const { error: goalError } = await supabase
                .from("user_goals")
                .insert({
                  user_id: userId,
                  ...goalsToSet,
                });
              
              if (goalError) {
                console.error("[PreAuthMetrics] Failed to insert built-in goals:", goalError);
              } else {
                console.log("[PreAuthMetrics] Successfully inserted built-in goals");
                successCount += builtInMetrics.length;
              }
            }
          }
        }

        // Clear pre-auth metrics after successful transfer (both local and email association)
        clearPreAuthMetrics();
        clearPreAuthEmail();
        localStorage.setItem(TRANSFER_FLAG_KEY, userId);
        
        // Mark backend metrics as transferred
        if (backendMetrics.length > 0) {
          try {
            await supabase.functions.invoke("mark-metrics-transferred");
            console.log("[PreAuthMetrics] Marked backend metrics as transferred");
          } catch (error) {
            console.error("[PreAuthMetrics] Failed to mark backend metrics as transferred:", error);
            // Not critical - cleanup will happen via expiration
          }
        }
        
        // Store HealthKit metrics for notification on dashboard
        if (healthKitMetrics.length > 0) {
          console.log("[PreAuthMetrics] Storing HealthKit metrics for notification:", healthKitMetrics);
          localStorage.setItem("gitfit_healthkit_metrics_pending", JSON.stringify(healthKitMetrics));
        }
        
        // Invalidate relevant queries to refresh the UI
        console.log("[PreAuthMetrics] Invalidating queries to refresh UI");
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ["custom-metrics", userId] }),
          queryClient.invalidateQueries({ queryKey: ["metric-preferences", userId] }),
          queryClient.invalidateQueries({ queryKey: ["user-goals", userId] }),
          // Also invalidate without userId in case any queries use that pattern
          queryClient.invalidateQueries({ queryKey: ["custom-metrics"] }),
          queryClient.invalidateQueries({ queryKey: ["metric-preferences"] }),
          queryClient.invalidateQueries({ queryKey: ["user-goals"] }),
        ]);
        
        console.log("[PreAuthMetrics] Waiting for cache invalidation to propagate...");
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Force refetch to ensure UI updates immediately
        console.log("[PreAuthMetrics] Refetching queries");
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["custom-metrics", userId] }),
          queryClient.refetchQueries({ queryKey: ["metric-preferences", userId] }),
          queryClient.refetchQueries({ queryKey: ["user-goals", userId] }),
        ]);
        
        console.log("[PreAuthMetrics] Refetch completed, data should be fresh");
        console.log("[PreAuthMetrics] Successfully transferred metrics");
      } catch (error) {
        console.error("[PreAuthMetrics] Error transferring metrics:", error);
        toast.error("Failed to transfer some metrics. Please add them manually.");
      }
    };

    // Small delay to ensure user account is fully set up
    const timeout = setTimeout(() => {
      transferMetrics();
    }, 1000);

    return () => clearTimeout(timeout);
  }, [userId]);
}
